import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { AnimatedRoutes } from "@/components/AnimationWrappers";
import { GlobalErrorBoundary } from "@/components/GlobalErrorBoundary";
import { MfaRouteEnforcer } from "@/components/MfaRouteEnforcer";

// Eager: homepage only — everything else is code-split for fast first paint on
// low-end phones. Each route ships its own JS chunk, fetched on navigation.
import Index from "./pages/Index.tsx";

const Dashboard = lazy(() => import("./pages/Dashboard.tsx"));
const TrackPage = lazy(() => import("./pages/TrackPage.tsx"));
const ModulePage = lazy(() => import("./pages/ModulePage.tsx"));
const ExamPage = lazy(() => import("./pages/ExamPage.tsx"));
const CertificatePage = lazy(() => import("./pages/CertificatePage.tsx"));
const MasterExamPage = lazy(() => import("./pages/MasterExamPage.tsx"));
const MasterCertificatePage = lazy(() => import("./pages/MasterCertificatePage.tsx"));
const VerifyCertificatePage = lazy(() => import("./pages/VerifyCertificatePage.tsx"));
const AuthPage = lazy(() => import("./pages/AuthPage.tsx"));
const MfaChallengePage = lazy(() => import("./pages/MfaChallengePage.tsx"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage.tsx"));
const ProfilePage = lazy(() => import("./pages/ProfilePage.tsx"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage.tsx"));
const PlaygroundPage = lazy(() => import("./pages/PlaygroundPage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const BlogIndexPage = lazy(() => import("./pages/BlogIndexPage.tsx"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage.tsx"));
const ComparePage = lazy(() => import("./pages/ComparePage.tsx"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage.tsx"));
const TermsPage = lazy(() => import("./pages/TermsPage.tsx"));
const AdminCryptoPage = lazy(() => import("./pages/AdminCryptoPage.tsx"));
import { RequireAuth } from "@/components/RequireAuth";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="font-mono text-sm text-primary animate-pulse">[ loading ]</div>
  </div>
);

const App = () => (
  <GlobalErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter
            future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
          >
            <MfaRouteEnforcer />
            <AnimatedRoutes>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/auth/mfa" element={<MfaChallengePage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                  <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
                  <Route path="/leaderboard" element={<LeaderboardPage />} />
                  <Route path="/track/:trackId" element={<TrackPage />} />
                  <Route path="/track/:trackId/module/:moduleId" element={<ModulePage />} />
                  <Route path="/exam/:trackId" element={<RequireAuth><ExamPage /></RequireAuth>} />
                  <Route path="/exam/master" element={<RequireAuth><MasterExamPage /></RequireAuth>} />
                  <Route path="/certificate/master" element={<RequireAuth><MasterCertificatePage /></RequireAuth>} />
                  <Route path="/verify/:code" element={<VerifyCertificatePage />} />
                  <Route path="/certificate/:trackId" element={<RequireAuth><CertificatePage /></RequireAuth>} />
                  <Route path="/playground" element={<PlaygroundPage />} />
                  <Route path="/blog" element={<BlogIndexPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/compare" element={<ComparePage />} />
                  <Route path="/vs/:platform" element={<ComparePage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/admin/crypto" element={<RequireAuth><AdminCryptoPage /></RequireAuth>} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AnimatedRoutes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </GlobalErrorBoundary>
);

export default App;
